import React, { useEffect } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Flex,
  Group,
  Loader,
  Menu,
  Popover,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconDots, IconX, IconChevronDown } from "@tabler/icons-react";
import { useStyles } from "../../../styles/TaxInfoSidebar.styles.ts";
import { useProfileStore } from "../../../store/profileStore.ts";

import { showNotification } from "@mantine/notifications";
import { useDocumentModalStore } from "@pages/ClientProfile/components/Sidebar/DocumentPopover/useDocumentModalStore.ts";
import type { DocumentItem } from "@pages/ClientProfile/types.ts";
import DocumentSectionItemPopover from "@pages/ClientProfile/components/Sidebar/DocumentPopover/DocumentSectionItemPopover.tsx";
import { OCRExtractionSelector } from "../OCRExtractionSelector";

interface DocumentItemProps {
  /**
   * The document to display.
   */
  item: DocumentItem;
  /**
   * Callback for when document gets selected.
   */
  onSelect: (url: string) => void;
}

/**
 * Displays a single document in the sidebar of the client profile.
 * @param item The document to display.
 * @param onSelect Callback for when a document is selected.
 */
export const DocumentGroupItem: React.FC<DocumentItemProps> = ({
  item,
  onSelect,
}) => {
  const { classes } = useStyles();
  const docId = item.docId ?? "";
  const {
    activeDocument,
    setActiveDocument,
    ocrProcessingDocId,
    setSelectedExtractionId,
    selectedExtractionIds,
    fetchOCRDataForDocument,
  } = useProfileStore();
  const { documentPopoverId, setDocumentPopoverId, openDocumentActions } =
    useDocumentModalStore();
  const isOpen = documentPopoverId === item.docId;

  // Check if document has multiple OCR extractions
  const hasMultipleExtractions =
    item.ocrExtractions && item.ocrExtractions.length > 1;
  const hasExtractions = item.ocrExtractions && item.ocrExtractions.length > 0;

  // Auto-select the latest extraction when document is first loaded
  useEffect(() => {
    if (hasExtractions && !selectedExtractionIds[docId]) {
      // Sort by createdAt and select the latest
      const sortedExtractions = [...item.ocrExtractions!].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setSelectedExtractionId(docId, sortedExtractions[0].id);
    }
  }, [
    docId,
    hasExtractions,
    selectedExtractionIds,
    item.ocrExtractions,
    setSelectedExtractionId,
  ]);

  const handleSelect = (): void => {
    try {
      // Trigger async lazy-load of OCR data (TaxInfoReview will re-render on cache update)
      fetchOCRDataForDocument(docId);
      setActiveDocument(item.docId || null);
      onSelect(item.presignedUrl);
    } catch (err) {
      console.error("Failed to select document or load OCR fields:", err);
      showNotification({
        title: "Error",
        message: "Failed to load document information.",
        color: "red",
      });
    }
  };

  const handleExtractionSelect = (extractionId: number) => {
    setSelectedExtractionId(docId, extractionId);
    fetchOCRDataForDocument(docId, extractionId);
  };

  const handleOptionsClick = () => {
    setActiveDocument(docId);
    openDocumentActions(); // Open the modal directly
  };

  const truncateFileName = (fileName: string, maxLength: number): string => {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.slice(fileName.lastIndexOf("."));
    const baseName = fileName.slice(0, fileName.lastIndexOf("."));
    const truncatedBaseName = baseName.slice(
      0,
      maxLength - extension.length - 3
    );
    return `${truncatedBaseName}...${extension}`;
  };

  return (
    <Tooltip
      label={item.label}
      position="right"
      disabled={item.label.length <= 35}
      multiline
      w={220}
      openDelay={500}
    >
      <Box
        className={
          activeDocument === item.docId
            ? classes.documentLinkActive
            : classes.documentLink
        }
        onClick={handleSelect}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && handleSelect()}
      >
        <div className={classes.documentTextContainer}>
          <Flex justify="space-between" align="center" w="100%">
            <Flex align="center" gap="xs">
              {item?.archived && (
                <Badge
                  size="xs"
                  radius="sm"
                  c="#7D4E00"
                  color="#FFE8C0"
                  style={{ flexShrink: 0, textTransform: "none" }}
                >
                  Archived
                </Badge>
              )}
              {item?.userType === "spouse" && (
                <Badge
                  size="xs"
                  radius="sm"
                  c="#0077CC"
                  color="#ACD3F0"
                  style={{ flexShrink: 0, textTransform: "none" }}
                >
                  Spouse
                </Badge>
              )}
              {item?.userType === "dependent" && (
                <Badge
                  size="xs"
                  radius="sm"
                  c="#8B5A2B"
                  color="#E4C49F"
                  style={{ flexShrink: 0, textTransform: "none" }}
                >
                  Dependent
                </Badge>
              )}
              <Group gap="xs" wrap="nowrap">
                <Text size="sm" className={classes.documentText}>
                  {truncateFileName(item.label, 25)}
                </Text>
                {hasMultipleExtractions && (
                  <Menu position="bottom-start" offset={5}>
                    <Menu.Target>
                      <ActionIcon
                        variant="subtle"
                        size="xs"
                        color="blue"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <IconChevronDown size={14} />
                      </ActionIcon>
                    </Menu.Target>
                    <OCRExtractionSelector
                      docId={docId}
                      extractions={item.ocrExtractions!}
                      onSelect={handleExtractionSelect}
                    />
                  </Menu>
                )}
              </Group>
            </Flex>

            <Popover
              width={200}
              withArrow
              shadow="md"
              opened={isOpen}
              zIndex={1998}
            >
              <Popover.Target>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color="gray"
                  onClick={(e) => {
                    e.stopPropagation(); // prevent triggering document selection
                    handleOptionsClick();
                  }}
                >
                  {ocrProcessingDocId === item.docId ? (
                    <Loader size="xs" />
                  ) : (
                    <IconDots />
                  )}
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown w={275}>
                <Box onClick={(e) => e.stopPropagation()}>
                  <Flex justify="flex-end" mb="xs">
                    <ActionIcon
                      size="xs"
                      variant="light"
                      color="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDocumentPopoverId(null); // close document popover.
                      }}
                      style={{ lineHeight: 1 }} // ensures tight vertical spacing
                      radius="xl"
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </Flex>
                  <DocumentSectionItemPopover />
                </Box>
              </Popover.Dropdown>
            </Popover>
          </Flex>
        </div>
      </Box>
    </Tooltip>
  );
};
